import { getIn, useFormikContext } from 'formik'

import { StSelectListContainer } from '../questionnaire-question/QuestionnaireQuestion.styled'
import { SelectListItem } from '../select-list-item'

import type { IQuestionnaireQuestion } from '@healthblocks-io/core'

type MultiSelectQuestionProps = {
  data: IQuestionnaireQuestion
  questionId: string
}

const MultiSelectQuestion = ({ data, questionId }: MultiSelectQuestionProps) => {
  const { setFieldValue, values } = useFormikContext<Record<string, unknown>>()

  // Get current value from Formik (always an array)
  // Use getIn to support dot-path field names (e.g. "allergie_positief.0.reactie") inside FieldArrays
  const currentValue = getIn(values, questionId)
  const selectedValues = Array.isArray(currentValue) ? currentValue : []

  const maxReached = data.max !== undefined && selectedValues.length >= data.max

  const handleOptionClick = (clickedValue: string) => {
    const clickedOption = data.answerOption?.find((option) => option.value === clickedValue)
    const isClickedOptionExclusive = (clickedOption as { exclusive?: boolean })?.exclusive === true

    const isCurrentlySelected = selectedValues.includes(clickedValue)

    let newValues: string[]

    // If deselecting, simply remove
    if (isCurrentlySelected) {
      newValues = selectedValues.filter((value) => value !== clickedValue)
    }
    // If selecting an exclusive option, clear all others
    else if (isClickedOptionExclusive) {
      newValues = [clickedValue]
    }
    // If selecting a regular option, remove any exclusive options first
    else {
      if (maxReached) return

      const exclusiveValues =
        data.answerOption
          ?.filter((option) => (option as { exclusive?: boolean }).exclusive)
          .map((option) => option.value) ?? []

      const withoutExclusive = selectedValues.filter((value) => !exclusiveValues.includes(value))

      newValues = [...withoutExclusive, clickedValue]
    }

    setFieldValue(questionId, newValues)
  }

  return (
    <StSelectListContainer>
      {data.answerOption?.map((option) => {
        const selected = selectedValues.includes(option.value ?? '')
        // Gray out unselected options once the max is reached; exclusive
        // options stay selectable because choosing one clears the others
        const isDisabled = !selected && maxReached && !(option as { exclusive?: boolean }).exclusive

        return (
          <SelectListItem
            type='checkbox'
            key={`${questionId}_${option.value}`}
            id={`${questionId}_${option.value}`}
            name={questionId}
            value={option.value}
            label={option.label}
            checked={selected}
            disabled={isDisabled}
            onChange={() => handleOptionClick(option.value ?? '')}
          />
        )
      })}
    </StSelectListContainer>
  )
}

export { MultiSelectQuestion }
